import "mapbox-gl/dist/mapbox-gl.css";
import "react-map-gl-geocoder/dist/mapbox-gl-geocoder.css";
import React, { useCallback, useEffect, useRef, useState } from "react";
import MapGL from "react-map-gl";
import Geocoder from "react-map-gl-geocoder";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import actions from "@redux/facility/actions";
import _ from "lodash";
import { Input } from "antd";
import { 
  getRegionFromGeocodingResult, 
  extractStateFromGeocodingResult,
  extractStateNameFromGeocodingResult,
  extractCityFromGeocodingResult,
  extractCountryFromGeocodingResult 
} from "@/constants/stateRegionMapping";

const AddressLookupWrapper = styled.div`
  height: 20vh;

  .mapboxgl-ctrl-geocoder {
    width: 100% !important;
    max-width: 100% !important;
  }
`;

const AddressLookup = (props) => {
  // 默认初始状态为空字符串，而不是props.value
  const [address, setAddress] = useState("");
  const { formRef } = props;
  const [viewport, setViewport] = useState({
    latitude: 37.7577,
    longitude: -122.4376,
    zoom: 14,
  });
  const dispatch = useDispatch();

  // 跟踪组件是否已挂载过
  const isFirstMount = useRef(true);
  const mapRef = useRef();
  const geocoderContainerRef = useRef();

  // 组件挂载时执行的逻辑
  useEffect(() => {
    // 仅在组件首次挂载时执行
    if (isFirstMount.current) {
      isFirstMount.current = false;

      // 如果是新表单（没有id字段或id为空），清空地址
      const isNewForm = !props.formRef?.getFieldValue?.("id");

      if (isNewForm) {
        // 清空Redux中的地址状态
        dispatch(actions.SelectedAddress(""));

        // 清空组件内部状态
        setAddress("");

        // 清空表单中的地址字段
        if (props.formRef && props.fieldName) {
          props.formRef.setFieldsValue({
            [props.fieldName]: "",
          });
        }
      } else if (props.value) {
        // 如果是编辑模式且有值，则设置地址
        setAddress(props.value);
      }
    }
  }, [dispatch, props.formRef, props.fieldName]);

  // 监听props.value的变化
  useEffect(() => {
    // 只有当props.value有值且组件不是首次挂载时，才更新地址
    if (
      !isFirstMount.current &&
      props.value &&
      props.value.length > 0 &&
      props.value !== null &&
      props.value !== undefined
    ) {
      console.log("props.value update:", props.value);
      setAddress(props.value);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value]);

  const handleViewportChange = useCallback(
    (newViewport) => {
      setViewport(newViewport);
      dispatch(actions.SelectedGeoLocation(newViewport));
    },
    [dispatch]
  );

  const onSelected = useCallback(
    (result) => {
      console.log("=== AddressLookup Debug Start ===");
      console.log("1. Address selected:", _.get(result, "result.place_name"));
      console.log("2. Full result object:", JSON.stringify(result, null, 2));
      
      let temp = _.get(result, "result.place_name");
      dispatch(actions.SelectedAddress(temp));
      setAddress(temp);
      
      // Extract all geographic information from geocoding result
      const geocodingResult = _.get(result, "result");
      console.log("3. Geocoding result:", JSON.stringify(geocodingResult, null, 2));
      
      // Check if context exists
      if (geocodingResult && geocodingResult.context) {
        console.log("4. Context array exists:", JSON.stringify(geocodingResult.context, null, 2));
      } else {
        console.log("4. Context array is missing!");
      }
      
      // Extract city
      const city = extractCityFromGeocodingResult(geocodingResult);
      console.log("5. Extracted city:", city);
      
      // Extract state (full name)
      const stateName = extractStateNameFromGeocodingResult(geocodingResult);
      console.log("6. Extracted state name:", stateName);
      
      // Extract country
      const country = extractCountryFromGeocodingResult(geocodingResult);
      console.log("7. Extracted country:", country);
      
      // Extract region (for US states only)
      const region = getRegionFromGeocodingResult(geocodingResult);
      console.log("8. Extracted region:", region);
      
      // Set all geographic fields
      const fieldsToSet = {
        [props.fieldName]: temp,
      };
      
      console.log("9. Initial fields to set:", fieldsToSet);
      
      // Auto-fill city if detected
      if (city) {
        fieldsToSet.city = city;
        console.log("10. City added to fields:", city);
      } else {
        console.log("10. City is null or undefined, not adding to fields");
      }
      
      // Auto-fill state if detected
      if (stateName) {
        fieldsToSet.state = stateName;
        console.log("11. State added to fields:", stateName);
      } else {
        console.log("11. State is null or undefined, not adding to fields");
      }
      
      // Auto-fill country if detected
      if (country) {
        fieldsToSet.country = country;
        console.log("12. Country added to fields:", country);
      } else {
        console.log("12. Country is null or undefined, not adding to fields");
      }
      
      // Auto-fill region if detected (for US states only)
      if (region) {
        fieldsToSet.region = region;
        console.log("13. Region added to fields:", region);
      } else {
        console.log("13. Region is null or undefined, not adding to fields");
      }
      
      console.log("14. Final fields to set:", fieldsToSet);
      console.log("15. FormRef exists:", !!formRef);
      console.log("16. Props.fieldName:", props.fieldName);
      
      if (formRef && formRef.setFieldsValue) {
        // First, set the address immediately
        formRef.setFieldsValue({
          [props.fieldName]: temp,
        });
        console.log("17. Address set immediately");
        
        // Then, set other geographic fields with a delay to ensure they are mounted
        setTimeout(() => {
          const geoFields = {};
          if (city) geoFields.city = city;
          if (stateName) geoFields.state = stateName;
          if (country) geoFields.country = country;
          if (region) geoFields.region = region;
          
          console.log("18. Setting geographic fields after delay:", geoFields);
          formRef.setFieldsValue(geoFields);
          
          // Verify the values were set
          setTimeout(() => {
            const currentValues = formRef.getFieldsValue();
            console.log("19. Current form values after set:", currentValues);
            console.log("20. City field value:", currentValues.city);
            console.log("21. State field value:", currentValues.state);
            console.log("22. Country field value:", currentValues.country);
            console.log("23. Region field value:", currentValues.region);
          }, 100);
        }, 200);
      } else {
        console.log("17. ERROR: formRef or setFieldsValue is not available!");
      }
      
      console.log("=== AddressLookup Debug End ===");
    },
    [dispatch, formRef, props.fieldName]
  );

  // if you are happy with Geocoder default settings, you can just use handleViewportChange directly
  const handleGeocoderViewportChange = useCallback(
    (newViewport) => {
      // console.log('newViewport', newViewport)
      const geocoderDefaultOverrides = { transitionDuration: 1000, zoom: 14 };
      return handleViewportChange({
        ...newViewport,
        ...geocoderDefaultOverrides,
      });
    },
    [handleViewportChange]
  );

  return (
    <AddressLookupWrapper>
      <div className={"mb-5"}>
        <Input disabled value={address} />
      </div>
      <div
        ref={geocoderContainerRef}
        style={{ position: "", top: -10, left: 20, zIndex: 1 }}
      />
      <MapGL
        ref={mapRef}
        {...viewport}
        width="100%"
        height="100px"
        onViewportChange={handleViewportChange}
        mapboxApiAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
        mapStyle="mapbox://styles/mapbox/streets-v11"
      >
        <Geocoder
          mapRef={mapRef}
          containerRef={geocoderContainerRef}
          onViewportChange={handleGeocoderViewportChange}
          mapboxApiAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
          position="top-left"
          trackProximity={true}
          collapsed={true}
          language="en"
          // inputValue={address}
          onResult={onSelected}
          expandFocused={false}
        />
      </MapGL>
    </AddressLookupWrapper>
  );
};

export default AddressLookup;
